import type { ComponentType } from '@/types/schemas/workspace'
import { AI_CHAT_KEYS } from '@/components/chat/ai-chat-shortcut'

/**
 * Static catalog of keyboard shortcuts, organized the way the cheat-sheet
 * renders it: PAGE tabs across the top, and within each page a "General"
 * section plus one section per PANEL type that exists on that page.
 *
 * This is the single display source of truth for now (read-only). When we add
 * remapping later, these become the DEFAULT bindings that a per-user override
 * store falls back to (and that "Restore defaults" resets to).
 *
 * Key tokens: `mod` (⌘/Ctrl), `alt` (⌥/Alt), `shift`, `ctrl`, named keys
 * (`tab`, `escape`, `backspace`, `enter`), the `1-9` range, and single letters.
 * See keycap.tsx for how tokens render.
 */

export interface ShortcutRow {
  action: string
  keys: string[]
}

export interface ShortcutSection {
  /** Heading for a non-panel section (e.g. "General"). Ignored when panelType is set. */
  title?: string
  /** When set, the section represents a panel type — icon + label come from COMPONENT_META. */
  panelType?: ComponentType
  rows: ShortcutRow[]
}

export interface ShortcutPage {
  key: string
  label: string
  /** Route prefixes that map to this page — used to pick the default tab. */
  routes: string[]
  sections: ShortcutSection[]
}

// Keys that work on every page, first in each page's General section. The
// voice chord is live for every user: its listener is attached even while the
// buddy is off, and hold-to-talk works once the buddy is on.
const EVERYWHERE: ShortcutRow[] = [
  { action: 'Open Edge AI (press again in the chat to close)', keys: AI_CHAT_KEYS },
  { action: 'Voice buddy: double-tap to turn on or off, then hold to talk', keys: ['ctrl', 'alt'] },
]

// Workspace-wide keys — apply on any mosaic page.
const WORKSPACE: ShortcutRow[] = [
  { action: 'Remove active panel', keys: ['mod', 'backspace'] },
  { action: "Hide hovered panel's top bar", keys: ['alt', 'b'] },
  { action: 'Hide all panel top bars', keys: ['alt', 'shift', 'b'] },
]

// Chart panel keys (hover the chart to use them). Bare typing is the entry
// layer (letters = symbol, digits = interval); tools and verbs are Option/Alt
// chords. Drawing-tool chords surface on their toolbar buttons and the
// right-click menu, so they are not repeated here.
const CHART: ShortcutRow[] = [
  { action: 'Change symbol (typed letter lands in the search)', keys: ['a-z'] },
  // Type a whole interval, then Enter: 1 2 3 5 10 15 30 (minutes), 60 or 1h,
  // 240 or 4h, 1d, 1w, 1M (capital M = month). Anything else does nothing.
  {
    action: 'Change interval: type 1, 2, 3, 5, 10, 15, 30, 60, 240, 1h, 4h, 1d, 1w or 1M',
    keys: ['interval', 'enter'],
  },
  {
    action: 'Drawing tool wheel: hold and release on a tool; tap to turn the tool off',
    keys: ['tab'],
  },
  { action: 'Expand / restore the chart within its panel', keys: ['space'] },
  { action: 'Maximize / restore the panel (works during replay)', keys: ['alt', 'enter'] },
  { action: 'Restore expanded chart, then exit maximize', keys: ['escape'] },
  { action: 'Drill down into cell', keys: ['shift', 'space'] },
  { action: 'Toggle drawing toolbar', keys: ['alt', 'a'] },
  { action: 'Show / hide drawings + indicators', keys: ['alt', 's'] },
  { action: 'Tag the bar under the cursor', keys: ['alt', 't'] },
  { action: 'Replay: play / pause', keys: ['space'] },
  { action: 'Replay: step forward one bar', keys: ['right'] },
  { action: 'Replay: playback speed', keys: ['up-down'] },
  { action: 'Replay: exit', keys: ['escape'] },
]

const NOTEBOOK: ShortcutRow[] = [{ action: 'Save now', keys: ['mod', 's'] }]

export const SHORTCUT_PAGES: ShortcutPage[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    routes: ['/dashboard'],
    sections: [
      { title: 'General', rows: [...EVERYWHERE, ...WORKSPACE] },
      { panelType: 'chart', rows: CHART },
      { panelType: 'notebook', rows: NOTEBOOK },
    ],
  },
  {
    key: 'history',
    label: 'History',
    routes: ['/history'],
    sections: [
      {
        title: 'General',
        rows: [
          ...EVERYWHERE,
          { action: 'Toggle layouts rail', keys: ['mod', 'b'] },
          { action: 'Jump to favorite layout', keys: ['alt', '1-9'] },
          ...WORKSPACE,
        ],
      },
      { panelType: 'chart', rows: CHART },
      { panelType: 'notebook', rows: NOTEBOOK },
    ],
  },
  {
    key: 'backtest',
    label: 'Backtest',
    routes: ['/backtester'],
    sections: [
      { title: 'General', rows: [...EVERYWHERE, ...WORKSPACE] },
      { panelType: 'chart', rows: CHART },
    ],
  },
]

/** Pick the page whose route prefix matches the current path; else the first page. */
export function pageKeyForPath(pathname: string): string {
  const match = SHORTCUT_PAGES.find((p) => p.routes.some((r) => pathname.startsWith(r)))
  return (match ?? SHORTCUT_PAGES[0]).key
}
